import React, { useState } from "react";
import { Copy, Check, Share2, Gift, Users } from "lucide-react";
import { toast } from "sonner";

// Referral hub: share a personal link and watch qualified referrals climb toward a reward.
export function ReferralCard({ referral }) {
  const [copied, setCopied] = useState(false);
  if (!referral) return null;

  const shareUrl = `${window.location.origin}/join?ref=${referral.code}`;
  const pct = Math.min((referral.qualified / referral.threshold) * 100, 100);
  const remaining = Math.max(referral.threshold - referral.qualified, 0);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — share it with a friend!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Long-press the link to copy it.");
    }
  };

  const share = async () => {
    const data = { title: "Psalm 30:2 Chiropractic", text: "I'd love for you to try Psalm 30:2 Chiropractic — book your first visit here:", url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(data); } catch (e) {
        // User dismissing the share sheet throws AbortError — that's expected, not an error.
        if (e?.name !== "AbortError" && process.env.NODE_ENV !== "production") console.debug("share failed:", e);
      }
    } else {
      copy();
    }
  };

  return (
    <div data-testid="referral-card" className="bg-espresso text-ivory rounded-2xl p-8 lg:p-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="grid place-items-center h-11 w-11 rounded-xl bg-brass/20"><Users size={20} className="text-brass" /></div>
        <div>
          <div className="overline !text-brass">Refer a Friend</div>
          <h3 className="font-serif text-2xl">Share care, earn rewards</h3>
        </div>
      </div>

      <p className="text-ivory/70 font-light leading-relaxed mb-6 max-w-lg">
        Send your personal link to friends and family. When they book and complete their first visit, you earn reward points — and after {referral.threshold} referrals you unlock a <span className="text-ivory">{referral.reward}</span>.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="flex-1 flex items-center bg-ivory/10 border border-ivory/15 rounded-full px-5 py-3 min-w-0" data-testid="referral-link">
          <span className="truncate text-sm text-ivory/85">{shareUrl}</span>
        </div>
        <div className="flex gap-2">
          <button data-testid="referral-copy" onClick={copy} className="inline-flex items-center gap-2 bg-ivory text-espresso rounded-full px-5 py-3 text-xs uppercase tracking-[0.16em] hover:bg-brass hover:text-ivory transition-colors">
            {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
          </button>
          <button data-testid="referral-share" onClick={share} className="inline-flex items-center gap-2 border border-ivory/30 rounded-full px-5 py-3 text-xs uppercase tracking-[0.16em] hover:bg-ivory/10 transition-colors">
            <Share2 size={15} /> Share
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-ivory/70">{Math.min(referral.qualified, referral.threshold)} of {referral.threshold} friends booked</span>
        <span className="inline-flex items-center gap-1.5 text-brass"><Gift size={15} /> {remaining === 0 ? "Reward ready!" : `${remaining} to go`}</span>
      </div>
      <div className="h-2.5 rounded-full bg-ivory/10 overflow-hidden" data-testid="referral-progress">
        <div className="h-full bg-gradient-to-r from-brass to-[#e0b567] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
